import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './sim-filter-panel.css';

/**
 * SimFilterPanel — панель фильтров SIM-карт (тёмная тема)
 * По макету Figma 26-04 wa Proxy Manager (node 436:93348, mobile 574:96774)
 */

// ==================== SVG ICONS ====================

// Иконка dropdown (стрелка вниз)
const DropdownIcon = ({ isOpen = false }) => (
  <svg
    className={`sfp__dropdown-icon ${isOpen ? 'sfp__dropdown-icon--open' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Иконка закрытия (крестик)
const CloseIcon = () => (
  <svg
    className="sfp__close-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ==================== CHIP COMPONENT ====================

const FilterChip = ({ label, count, isActive, onClick, countColor }) => (
  <button
    type="button"
    className={`sfp__chip ${isActive ? 'sfp__chip--active' : ''}`}
    onClick={onClick}
    tabIndex={0}
  >
    <span className="sfp__chip-label">{label}</span>
    <span className={`sfp__chip-count${countColor ? ` sfp__chip-count--${countColor}` : ''}`}>{count}</span>
  </button>
);

// ==================== FILTER SECTION ====================

const FilterSection = ({ title, items, selectedValues, onToggle }) => (
  <div className="sfp__block">
    <h3 className="sfp__title">{title}</h3>
    <div className="sfp__chips">
      {items.map((item) => (
        <FilterChip
          key={item.value}
          label={item.label}
          count={item.count}
          countColor={item.countColor}
          isActive={selectedValues.includes(item.value)}
          onClick={() => onToggle(item.value)}
        />
      ))}
    </div>
  </div>
);

// ==================== DROPDOWN COMPONENT ====================

const FilterDropdown = ({ label, values = [], options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSearch('');
      }
      return next;
    });
  };

  const handleToggleOption = (optionValue) => {
    const next = values.includes(optionValue)
      ? values.filter((v) => v !== optionValue)
      : [...values, optionValue];
    if (onChange) {
      onChange(next);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const displayValue = values.length > 0
    ? `${label} (${values.length})`
    : label;
  const filteredOptions = options.filter((option) =>
    String(option.label || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={`sfp__dropdown${isOpen ? ' sfp__dropdown--open' : ''}`} ref={ref}>
      <button
        type="button"
        className="sfp__dropdown-field"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        tabIndex={0}
      >
        <span className={`sfp__dropdown-text ${values.length > 0 ? 'sfp__dropdown-text--filled' : ''}`}>
          {displayValue}
        </span>
        <DropdownIcon isOpen={isOpen} />
      </button>
      {isOpen && (
        <div className="sfp__dropdown-list sfp__dropdown-list--multi" role="listbox">
          <div className="sfp__dropdown-search">
            <input
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="sfp__dropdown-search-input"
              aria-label={`Поиск: ${label}`}
            />
          </div>
          {values.length > 0 && (
            <button
              type="button"
              className="sfp__dropdown-option sfp__dropdown-option--reset"
              onClick={() => onChange && onChange([])}
              tabIndex={0}
            >
              Сбросить выбор
            </button>
          )}
          {filteredOptions.map((option) => (
            <label
              key={option.value}
              className="sfp__dropdown-multi-item"
            >
              <input
                type="checkbox"
                checked={values.includes(option.value)}
                onChange={() => handleToggleOption(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
          {filteredOptions.length === 0 && (
            <div className="sfp__dropdown-empty">Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MULTI-SELECT DROPDOWN WITH SEARCH ====================
const FilterMultiDropdown = ({ label, values = [], options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleOption = (val) => {
    const next = values.includes(val) ? values.filter((v) => v !== val) : [...values, val];
    if (onChange) onChange(next);
  };

  const handleClearAll = () => {
    if (onChange) onChange([]);
    setIsOpen(false);
  };

  const displayValue = values.length > 0
    ? `${label} (${values.length})`
    : label;

  return (
    <div className={`sfp__dropdown${isOpen ? ' sfp__dropdown--open' : ''}`} ref={ref}>
      <button
        type="button"
        className="sfp__dropdown-field"
        onClick={() => setIsOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        tabIndex={0}
      >
        <span className={`sfp__dropdown-text ${values.length > 0 ? 'sfp__dropdown-text--filled' : ''}`}>
          {displayValue}
        </span>
        <DropdownIcon isOpen={isOpen} />
      </button>
      {isOpen && (
        <div className="sfp__dropdown-list sfp__dropdown-list--multi" role="listbox">
          <div className="sfp__dropdown-search">
            <input
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="sfp__dropdown-search-input"
            />
          </div>
          {values.length > 0 && (
            <button
              type="button"
              className="sfp__dropdown-option sfp__dropdown-option--reset"
              onClick={handleClearAll}
              tabIndex={0}
            >
              Сбросить выбор
            </button>
          )}
          {filtered.length === 0 && (
            <div className="sfp__dropdown-empty">Ничего не найдено</div>
          )}
          {filtered.map((option) => (
            <label key={option.value} className="sfp__dropdown-multi-item">
              <input
                type="checkbox"
                checked={values.includes(option.value)}
                onChange={() => handleToggleOption(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const SimFilterPanel = ({
  isVisible = true,
  onApply,
  onReset,
  onClose,
  onServerFilterDraftChange,
  isMobile = false,
  // Данные для фильтров (из API)
  modemStatusCounts = [
    { value: 'available', label: 'Доступен', count: 4, countColor: 'green' },
    { value: 'unavailable', label: 'Недоступен', count: 1, countColor: 'red' },
    { value: 'unknown', label: 'Неизвестно', count: 1, countColor: 'white' },
    { value: 'disabled', label: 'Отключен', count: 0, countColor: 'red' },
  ],
  simStatusCounts = [
    { value: 'working', label: 'Работает', count: 1, countColor: 'green' },
    { value: 'replace', label: 'Заменить', count: 1, countColor: 'red' },
    { value: 'check', label: 'Проверить', count: 1, countColor: 'yellow' },
    { value: 'free', label: 'Свободна', count: 1, countColor: 'blue' },
    { value: 'reserve_msk', label: 'Резерв Мск', count: 1, countColor: 'white' },
    { value: 'reserve_spb', label: 'Резерв СПб', count: 1, countColor: 'white' },
  ],
  geoCounts = [
    { value: 'msk', label: 'Мск', count: 1, countColor: 'gray' },
    { value: 'spb', label: 'СПб', count: 5, countColor: 'gray' },
  ],
  paymentCounts = [
    { value: 'today', label: 'Сегодня', count: 1, countColor: 'red' },
    { value: 'tomorrow', label: 'Завтра', count: 1, countColor: 'blue' },
    { value: '3days', label: 'Через 3 дня', count: 1, countColor: 'white' },
  ],
  serverOptions = [
    { value: 'TestServer_1', label: 'TestServer_1' },
    { value: 'TestServer_2', label: 'TestServer_2' },
  ],
  modemNumberOptions = [
    { value: '12', label: '12' },
    { value: '13', label: '13' },
    { value: '14', label: '14' },
    { value: '15', label: '15' },
    { value: '16', label: '16' },
    { value: '200', label: '200' },
  ],
  modemNumberOptionsByServer = {},
  phoneNumberOptions = [],
  registeredByOptions = [
    { value: 'corp', label: 'Корп' },
    { value: 'ivanov', label: 'Иванов А.А.' },
    { value: 'sidorov', label: 'Сидоров С.А.' },
  ],
  personalAccountOwnerOptions = [],
  operatorOptions = [],
}) => {
  // Состояние для анимации закрытия
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isVisible);
  const prevVisibleRef = useRef(isVisible);

  // Обработка изменения isVisible
  useEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = isVisible;

    if (isVisible && !wasVisible) {
      // Открытие
      setShouldRender(true);
      setIsClosing(false);
    } else if (!isVisible && wasVisible) {
      // Закрытие — запускаем анимацию
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 450);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Состояние фильтров
  const [filters, setFilters] = useState({
    modemStatus: [],
    simStatus: [],
    geo: [],
    payment: [],
    operator: [],
    server: [],
    modemNumber: [],
    phoneNumber: [],
    registeredBy: [],
    personalAccountOwner: [],
  });

  // Переключение чипа (множественный выбор)
  const handleChipToggle = useCallback((filterKey, value) => {
    setFilters((prev) => {
      const current = prev[filterKey];
      const newValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [filterKey]: newValues };
    });
  }, []);

  // Закрытие (для мобильного overlay)
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Сброс фильтров
  const handleReset = useCallback(() => {
    setFilters({
      modemStatus: [],
      simStatus: [],
      geo: [],
      payment: [],
      operator: [],
      server: [],
      modemNumber: [],
      phoneNumber: [],
      registeredBy: [],
      personalAccountOwner: [],
    });
    if (onReset) {
      onReset();
    }
  }, [onReset]);

  // Применение фильтров
  const handleApply = useCallback(() => {
    if (onApply) {
      onApply(filters);
    }
    if (isMobile) {
      handleClose();
    }
  }, [onApply, filters, isMobile, handleClose]);

  const availableModemOptions = (filters.server || []).length > 0
    ? (() => {
        const collected = [];
        const seen = new Set();
        (filters.server || []).forEach((serverName) => {
          const options = modemNumberOptionsByServer[serverName] || [];
          options.forEach((option) => {
            if (seen.has(option.value)) {
              return;
            }
            seen.add(option.value);
            collected.push(option);
          });
        });
        return collected;
      })()
    : modemNumberOptions;

  const availablePhoneOptions = (() => {
    let options = phoneNumberOptions;

    if ((filters.server || []).length > 0) {
      const allowedServers = new Set(filters.server);
      options = options.filter((option) =>
        !option.serverName || allowedServers.has(option.serverName)
      );
    }

    if ((filters.modemNumber || []).length > 0) {
      const allowedModems = new Set(filters.modemNumber);
      options = options.filter((option) => {
        if (!Array.isArray(option.modemUiValues) || option.modemUiValues.length === 0) {
          return false;
        }
        return option.modemUiValues.some((value) => allowedModems.has(value));
      });
    }

    return options;
  })();

  // Клик по оверлею (мобильный) — только настоящий: нажатие и отпускание на нём (просьба владельца 28.10)
  const overlayBackdrop = useBackdropClose(handleClose);

  // Escape закрытие (мобильный)
  useEffect(() => {
    if (!isMobile || !isVisible) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobile, isVisible, handleClose]);

  useEffect(() => {
    if (!onServerFilterDraftChange) {
      return;
    }
    onServerFilterDraftChange(filters.server || []);
  }, [filters.server, onServerFilterDraftChange]);

  // Блокируем прокрутку страницы, пока открыт мобильный фильтр.
  useEffect(() => {
    if (!isMobile || !shouldRender) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobile, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  // Общее содержимое фильтра
  const filterContent = (
    <>
      {/* Модем */}
      <FilterSection
        title="Модем"
        items={modemStatusCounts}
        selectedValues={filters.modemStatus}
        onToggle={(value) => handleChipToggle('modemStatus', value)}
      />

      {/* SIM */}
      <FilterSection
        title="SIM"
        items={simStatusCounts}
        selectedValues={filters.simStatus}
        onToggle={(value) => handleChipToggle('simStatus', value)}
      />

      {/* Гео */}
      <FilterSection
        title="Гео"
        items={geoCounts}
        selectedValues={filters.geo}
        onToggle={(value) => handleChipToggle('geo', value)}
      />

      {/* Следующий платеж */}
      <FilterSection
        title="Следующий платеж"
        items={paymentCounts}
        selectedValues={filters.payment}
        onToggle={(value) => handleChipToggle('payment', value)}
      />

      {/* Оператор — сразу после «Следующий платеж» */}
      <FilterDropdown
        label="Оператор"
        values={filters.operator}
        options={operatorOptions}
        onChange={(vals) => setFilters((prev) => ({ ...prev, operator: vals }))}
      />

      {/* Сервер */}
      <FilterDropdown
        label="Сервер"
        values={filters.server}
        options={serverOptions}
        onChange={(vals) => {
          setFilters((prev) => {
            const nextServer = vals;
            const collected = [];
            const seen = new Set();
            nextServer.forEach((serverName) => {
              const options = modemNumberOptionsByServer[serverName] || [];
              options.forEach((option) => {
                if (seen.has(option.value)) {
                  return;
                }
                seen.add(option.value);
                collected.push(option.value);
              });
            });
            const allowed = new Set(collected);
            const nextModemNumber = nextServer.length > 0
              ? prev.modemNumber.filter((value) => allowed.has(value))
              : prev.modemNumber;

            let nextPhoneNumber = prev.phoneNumber;
            if (nextServer.length > 0) {
              const allowedServers = new Set(nextServer);
              nextPhoneNumber = prev.phoneNumber.filter((phoneValue) =>
                phoneNumberOptions.some((option) =>
                  option.value === phoneValue
                    && (!option.serverName || allowedServers.has(option.serverName))
                    && (
                      nextModemNumber.length === 0
                      || (Array.isArray(option.modemUiValues)
                        && option.modemUiValues.some((value) => nextModemNumber.includes(value)))
                    )
                )
              );
            }

            return {
              ...prev,
              server: nextServer,
              modemNumber: nextModemNumber,
              phoneNumber: nextPhoneNumber,
            };
          });
        }}
      />

      {/* Номер модема */}
      <FilterDropdown
        label="Номер модема"
        values={filters.modemNumber}
        options={availableModemOptions}
        onChange={(vals) => {
          setFilters((prev) => {
            const nextPhoneNumber = prev.phoneNumber.filter((phoneValue) =>
              phoneNumberOptions.some((option) =>
                option.value === phoneValue
                  && (vals.length === 0
                    || (Array.isArray(option.modemUiValues)
                      && option.modemUiValues.some((value) => vals.includes(value))))
              )
            );
            return { ...prev, modemNumber: vals, phoneNumber: nextPhoneNumber };
          });
        }}
      />

      {/* Номер телефона */}
      <FilterMultiDropdown
        label="Номер"
        values={filters.phoneNumber}
        options={availablePhoneOptions}
        onChange={(vals) => setFilters((prev) => ({ ...prev, phoneNumber: vals }))}
      />

      {/* Оформлена */}
      <FilterDropdown
        label="Оформлена"
        values={filters.registeredBy}
        options={registeredByOptions}
        onChange={(vals) => setFilters((prev) => ({ ...prev, registeredBy: vals }))}
      />

      {/* Личный кабинет */}
      <FilterDropdown
        label="Личный кабинет"
        values={filters.personalAccountOwner}
        options={personalAccountOwnerOptions}
        onChange={(vals) => setFilters((prev) => ({ ...prev, personalAccountOwner: vals }))}
      />
    </>
  );

  // Мобильный режим — overlay bottom-sheet
  if (isMobile) {
    const mobileOverlay = (
      <div
        className="sfp-overlay"
        {...overlayBackdrop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sfp-title"
      >
        <div className="sfp sfp--mobile">
          {/* Header */}
          <div className="sfp__header">
            <h2 className="sfp__header-title" id="sfp-title">
              Фильтр
            </h2>
            <button
              type="button"
              className="sfp__close-btn"
              onClick={handleClose}
              aria-label="Закрыть"
              tabIndex={0}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Скроллируемый контент */}
          <div className="sfp__scroll-content">
            {filterContent}
          </div>

          {/* Кнопки */}
          <div className="sfp__buttons">
            <button
              type="button"
              className="sfp__btn sfp__btn--reset"
              onClick={handleReset}
              tabIndex={0}
            >
              Сбросить
            </button>
            <button
              type="button"
              className="sfp__btn sfp__btn--apply"
              onClick={handleApply}
              tabIndex={0}
            >
              Применить
            </button>
          </div>
        </div>
      </div>
    );

    if (typeof document === 'undefined') {
      return mobileOverlay;
    }

    return createPortal(mobileOverlay, document.body);
  }

  // Десктопный режим — боковая панель (без изменений)
  return (
    <div className={`sfp-wrapper ${isClosing ? 'sfp-wrapper--closing' : ''}`}>
      <div className={`sfp ${isClosing ? 'sfp--closing' : ''}`}>
      {filterContent}

      {/* Кнопки */}
      <div className="sfp__buttons">
        <button
          type="button"
          className="sfp__btn sfp__btn--reset"
          onClick={handleReset}
          tabIndex={0}
        >
          Сбросить
        </button>
        <button
          type="button"
          className="sfp__btn sfp__btn--apply"
          onClick={handleApply}
          tabIndex={0}
        >
          Применить
        </button>
      </div>
      </div>
    </div>
  );
};

export default SimFilterPanel;
